import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import comentarioService from '../../services/comentario.service';
import usuarioService from '../../services/usuario.service';  // r213: citáveis (qualquer usuário)
import ComentarioModal from './ComentarioModal';   // r173: composição em modal
import apontamentoService from '../../services/apontamento.service';   // r181

// ════════════════════════════════════════════════════════════════════════════
// <ChatComentarios> — chat estilo Slack/Teams pra tarefas (r35 item 6)
// ────────────────────────────────────────────────────────────────────────────
// - Lista cronológica de mensagens (mais recentes em baixo)
// - Cada mensagem: avatar, nome, tempo, corpo markdown, anexos
// - Editor na base com:
//   - Textarea expansível
//   - Ctrl+V cola imagem do clipboard (faz upload e insere ![alt](url) no corpo)
//   - Botão 📎 anexa qualquer arquivo (upload + adiciona no array de anexos)
//   - Preview de anexos antes de enviar
//   - Enter envia (Shift+Enter pula linha)
//
// Imagens inline: o markdown `![alt](url)` é renderizado como <img>.
// Anexos não-imagem aparecem como badges com link de download no rodapé.
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',    cinzaBg: '#fafafa', cinzaSt: '#f3f4f6',
  verde:   '#10b981',
  // r181: mesmo verde das horas apontadas no browse de tarefas
  verdeHoras: '#008700', verdeBg: '#f2fbf4',
  vermelho:'#dc2626',
};

// ─── r200: preferências do chat, por usuário ─────────────────────────────
// Ordem (crescente/decrescente) e filtro (comentários / horas / todos) ficam
// no localStorage por usuário — mesmo esquema dos filtros de Lançamentos.
const CHAVE_PREFS = (uid) => `tarefas.chat.prefs.${uid || 'anon'}`;

// Padrão: cronológico como sempre foi, e mostrando SÓ comentários.
const PREFS_DEFAULT = { ordem: 'asc', filtro: 'C' };

// Filtro: C = comentários · H = lançamento de horas · T = todos
const FILTRO_OPCOES = [
  { valor: 'C', label: 'Comentários' },
  { valor: 'H', label: 'Horas' },
  { valor: 'T', label: 'Todos' },
];

// Lê e sanitiza — valor estranho (versão antiga, chave adulterada) cai no default.
function lerPrefs(uid) {
  try {
    const obj = JSON.parse(localStorage.getItem(CHAVE_PREFS(uid)) || "null");
    if (!obj || typeof obj !== 'object') return PREFS_DEFAULT;
    return {
      ordem:  (obj.ordem === 'desc' || obj.ordem === 'asc') ? obj.ordem : PREFS_DEFAULT.ordem,
      filtro: FILTRO_OPCOES.some(o => o.valor === obj.filtro) ? obj.filtro : PREFS_DEFAULT.filtro,
    };
  } catch (_) {
    return PREFS_DEFAULT;
  }
}

function tempoRelativo(iso) {
  if (!iso) return '';
  const data = new Date(iso);
  const agora = new Date();
  const diffMs = agora - data;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1)   return 'agora';
  if (diffMin < 60)  return `há ${diffMin}min`;
  const diffHora = Math.floor(diffMin / 60);
  if (diffHora < 24) return `há ${diffHora}h`;
  const diffDia = Math.floor(diffHora / 24);
  if (diffDia < 7)   return `há ${diffDia}d`;
  return data.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit',
                                          hour: '2-digit', minute: '2-digit' });
}

// ─── r181: helpers dos apontamentos exibidos no chat ─────────────────────
// O banco guarda horas em numeric(5,2): 6.5 é seis horas e meia, não 6h50.
function horasParaHHMM(dec) {
  const totalMin = Math.round((Number(dec) || 0) * 60);
  return `${String(Math.floor(totalMin / 60)).padStart(2, '0')}:${String(totalMin % 60).padStart(2, '0')}`;
}

// 'dd/mm/aaaa - hh:mm'
function dataHora(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.toLocaleDateString('pt-BR')} - ${hh}:${mm}`;
}

// faturavel é guardado como 1/0 (nunca boolean) — ver CLAUDE.md
function ehFaturavel(v) {
  return v === 1 || v === '1' || v === true || v === 'S' || v === 's';
}

// Tag do cabeçalho do apontamento
function TagApontamento({ texto, cor, solida }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      height: '18px', padding: '0 8px', borderRadius: '9px',
      fontSize: '10px', fontWeight: '700', whiteSpace: 'nowrap',
      border: `1px solid ${cor}`,
      backgroundColor: solida ? cor : cor + '15',
      color: solida ? COR.branco : cor,
    }}>{texto}</span>
  );
}

// ─── Renderizador simples de markdown ────────────────────────────────────
// Converte `![alt](url)` em <img>, `[texto](url)` em <a>, `**neg**` em <b>,
// `*ita*` em <i>, ``code`` em <code>, quebras de linha em <br/>
function renderizarMarkdown(texto) {
  if (!texto) return null;
  const partes = [];
  let resto = texto;
  let chave = 0;

  // Padrão pra imagem: ![alt](url)
  const reImg = /!\[([^\]]*)\]\(([^)]+)\)/;
  // Link: [texto](url)
  const reLink = /\[([^\]]+)\]\(([^)]+)\)/;

  while (resto.length > 0) {
    const imgMatch = resto.match(reImg);
    const linkMatch = resto.match(reLink);

    let proxMatch = null;
    let tipo = null;
    if (imgMatch && (!linkMatch || imgMatch.index <= linkMatch.index)) {
      proxMatch = imgMatch; tipo = 'img';
    } else if (linkMatch) {
      proxMatch = linkMatch; tipo = 'link';
    }

    if (!proxMatch) {
      // Resto é texto puro (com bold/italic/code simples)
      partes.push(<TextoFormatado key={chave++} texto={resto} />);
      break;
    }

    // Texto antes do match
    if (proxMatch.index > 0) {
      partes.push(<TextoFormatado key={chave++} texto={resto.slice(0, proxMatch.index)} />);
    }

    if (tipo === 'img') {
      partes.push(
        <img key={chave++}
             src={proxMatch[2]}
             alt={proxMatch[1] || 'imagem'}
             style={{
               maxWidth: '100%', maxHeight: '300px',
               borderRadius: '6px', margin: '4px 0',
               display: 'block', cursor: 'zoom-in',
               border: `1px solid ${COR.borda}`,
             }}
             onClick={() => window.open(proxMatch[2], '_blank')}
        />
      );
    } else {
      partes.push(
        <a key={chave++}
           href={proxMatch[2]}
           target="_blank" rel="noopener noreferrer"
           style={{ color: COR.azul, textDecoration: 'underline' }}>
          {proxMatch[1]}
        </a>
      );
    }
    resto = resto.slice(proxMatch.index + proxMatch[0].length);
  }
  return partes;
}

// Formata bold/italic/code e quebras de linha
function TextoFormatado({ texto }) {
  const linhas = texto.split('\n');
  return (
    <>
      {linhas.map((linha, i) => (
        <React.Fragment key={i}>
          {aplicarInline(linha)}
          {i < linhas.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  );
}

function aplicarInline(texto) {
  // **bold**, *italic*, `code`
  // Implementação leve sem regex complexo
  const partes = [];
  let resto = texto;
  let k = 0;
  while (resto.length > 0) {
    const mBold = resto.match(/\*\*([^*]+)\*\*/);
    const mIta  = resto.match(/\*([^*]+)\*/);
    const mCode = resto.match(/`([^`]+)`/);
    let prox = null, tipo = null;
    [
      { m: mBold, t: 'b' },
      { m: mIta,  t: 'i' },
      { m: mCode, t: 'code' },
    ].forEach(({ m, t }) => {
      if (m && (!prox || m.index < prox.index)) { prox = m; tipo = t; }
    });
    if (!prox) { partes.push(resto); break; }
    if (prox.index > 0) partes.push(resto.slice(0, prox.index));
    if (tipo === 'b')        partes.push(<b key={k++}>{prox[1]}</b>);
    else if (tipo === 'i')   partes.push(<i key={k++}>{prox[1]}</i>);
    else                     partes.push(<code key={k++} style={{
      backgroundColor: COR.cinzaSt, padding: '1px 4px',
      borderRadius: '3px', fontFamily: 'monospace', fontSize: '11px',
    }}>{prox[1]}</code>);
    resto = resto.slice(prox.index + prox[0].length);
  }
  return partes;
}

// ─── <AnexoBadge> — badge clicável pra anexos não-imagem ──────────────────
function AnexoBadge({ anexo }) {
  const formatarTamanho = (b) => {
    if (!b) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };
  const icone = (anexo.mime || '').startsWith('image/') ? '🖼️'
              : (anexo.mime || '').includes('pdf') ? '📄'
              : (anexo.mime || '').includes('sheet') ? '📊'
              : (anexo.mime || '').includes('word') ? '📝'
              : '📎';
  // r183: `download` em imagem faz o browser abrir uma aba em branco e baixar
  // o arquivo em vez de exibi-lo. Imagem abre normal; o resto continua baixando.
  const ehImagem = (anexo.mime || '').startsWith('image/');
  return (
    <a href={anexo.url} target="_blank" rel="noopener noreferrer"
       download={ehImagem ? undefined : anexo.nome_original}
       style={{
         display: 'inline-flex', alignItems: 'center', gap: '6px',
         padding: '5px 10px',
         backgroundColor: COR.cinzaBg,
         border: `1px solid ${COR.borda}`,
         borderRadius: '4px',
         textDecoration: 'none',
         color: COR.texto, fontSize: '11px',
         marginTop: '4px', marginRight: '4px',
         fontFamily: "'Manrope', sans-serif",
       }}>
      <span style={{ fontSize: '13px' }}>{icone}</span>
      <span style={{ fontWeight: '600' }}>{anexo.nome_original}</span>
      {anexo.tamanho_bytes ? (
        <span style={{ color: COR.muted, fontSize: '10px' }}>
          ({formatarTamanho(anexo.tamanho_bytes)})
        </span>
      ) : null}
    </a>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// <ChatComentarios> — componente principal
// ════════════════════════════════════════════════════════════════════════════
function ChatComentarios({ tarefaId, podeComentar = true, mencionaveis = [] }) {
  const { usuario } = useAuth();
  const [comentarios, setComentarios] = useState([]);
  const [apontamentos, setApontamentos] = useState([]);   // r181: horas no chat
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  // r200: ordem + filtro, lembrados por usuário
  const [prefs, setPrefs] = useState(PREFS_DEFAULT);
  useEffect(() => { setPrefs(lerPrefs(usuario?.id)); }, [usuario?.id]);
  const salvarPrefs = (mudanca) => {
    const novas = { ...prefs, ...mudanca };
    setPrefs(novas);
    try {
      localStorage.setItem(CHAVE_PREFS(usuario?.id), JSON.stringify(novas));
    } catch (err) {
      console.warn('Não foi possível salvar as preferências do chat:', err);
    }
  };

  // r173: composição de comentário agora é em modal (novo / responder)
  const [modal, setModal] = useState(null);   // null | { parentId }
  const listaRef = useRef(null);

  // r213: citáveis (@) = QUALQUER usuário ativo, não só solicitante e
  //       responsável. Vem de /usuarios/referencia (rota autenticada, sem
  //       checagem de rotina), que já esconde o usuário de sistema. O citado
  //       recebe notificação e passa a enxergar a tarefa.
  //       O prop `mencionaveis` continua aceito, mas agora é só a semente
  //       exibida enquanto a lista completa não chega.
  const [mencs, setMencs] = useState(mencionaveis || []);
  useEffect(() => {
    let ativo = true;
    usuarioService.listarReferencia()
      .then(us => {
        if (!ativo) return;
        setMencs((us || [])
          .filter(u => u.email)
          .map(u => ({ nome: u.nome_amigavel || u.nome, email: u.email })));
      })
      .catch(err => {
        // Degrada pro que veio por prop — pior citar pouco do que travar o chat.
        console.warn('Não foi possível carregar a lista de citáveis:', err.message);
      });
    return () => { ativo = false; };
  }, []);

  // ── Carrega comentários ──────────────────────────────────────────────────
  const recarregar = useCallback(async () => {
    if (!tarefaId) return;
    setCarregando(true);
    setErro('');

    // r201: cada lista falha por conta própria. Antes era um Promise.all com
    // catch só nos apontamentos: se a busca dos COMENTÁRIOS desse erro, o
    // Promise.all rejeitava, nenhum dos dois setState rodava e a tela ficava
    // vazia — com o motivo só no console. Agora o erro aparece pro usuário.
    const [resComentarios, resApontamentos] = await Promise.allSettled([
      comentarioService.listar(tarefaId),
      apontamentoService.listar(tarefaId),
    ]);

    if (resComentarios.status === 'fulfilled') {
      setComentarios(Array.isArray(resComentarios.value) ? resComentarios.value : []);
    } else {
      const err = resComentarios.reason;
      const status = err?.response?.status;
      setComentarios([]);
      setErro(
        'Não foi possível carregar os comentários' +
        (status ? ` (HTTP ${status})` : '') + '. ' +
        (err?.response?.data?.mensagem || err?.message || '')
      );
      console.warn('Erro ao listar comentários:', err);
    }

    // Apontamentos seguem best-effort: sem eles o chat ainda serve.
    setApontamentos(resApontamentos.status === 'fulfilled' && Array.isArray(resApontamentos.value)
      ? resApontamentos.value
      : []);
    if (resApontamentos.status === 'rejected') {
      console.warn('Erro ao listar apontamentos:', resApontamentos.reason);
    }

    setCarregando(false);
  }, [tarefaId]);

  useEffect(() => { recarregar(); }, [recarregar]);

  // Auto-scroll pro lado onde estão as mensagens mais novas. Em ordem
  // decrescente elas ficam no TOPO, então rolar pro fim levaria pro mais antigo.
  useEffect(() => {
    if (!listaRef.current) return;
    listaRef.current.scrollTop = prefs.ordem === 'desc' ? 0 : listaRef.current.scrollHeight;
  }, [comentarios.length, apontamentos.length, prefs.ordem, prefs.filtro]);

  // ── Deletar comentário (só autor) ───────────────────────────────────────
  const deletar = async (cid) => {
    if (!window.confirm('Apagar este comentário?')) return;
    try {
      await comentarioService.deletar(cid, usuario.id);
      await recarregar();
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Erro ao deletar.');
    }
  };

  // ── Renderiza corpo substituindo __INLINE_n__ pelas URLs dos anexos inline
  const corpoParaExibir = (com) => {
    const corpo = com.corpo || '';
    if (!corpo.includes('__INLINE_')) return corpo;

    const todos   = com.anexos || [];
    const inlines = todos.filter(a => a.inline);
    // r183: se o flag `inline` nao veio (comentario antigo ou gravado errado),
    // cai pras imagens do comentario na ordem em que foram anexadas — o indice
    // do placeholder foi gerado exatamente nessa ordem.
    const base = inlines.length > 0
      ? inlines
      : todos.filter(a => (a.mime || '').startsWith('image/'));

    // Placeholder nao resolvido some do corpo: melhor nada do que um
    // `<img src="__INLINE_0__">` quebrado que abre aba em branco ao clicar.
    return corpo.replace(/!\[([^\]]*)\]\(__INLINE_(\d+)__\)|__INLINE_(\d+)__/g,
      (trecho, alt, nMarkdown, nSolto) => {
        const anx = base[Number(nMarkdown !== undefined ? nMarkdown : nSolto)];
        if (!anx) return '';
        return `![${alt || anx.nome_original}](${anx.url})`;
      });
  };

  // r173: renderiza um comentário (raiz ou resposta aninhada)
  const renderComentario = (c, ehResposta) => {
    const ehMeu = c.usuario_id === usuario?.id;
    const inicial = (c.nome_amigavel || c.nome || '?').charAt(0).toUpperCase();
    const anexosNaoInline = (c.anexos || []).filter(a => !a.inline);
    return (
      <div key={c.id} style={{
        display: 'flex', gap: '10px',
        marginBottom: ehResposta ? '8px' : '10px',
        marginLeft: ehResposta ? '30px' : 0,
        padding: '8px 10px',
        backgroundColor: ehResposta ? '#fdf3e7' : (ehMeu ? COR.azulBg : COR.cinzaBg),
        borderRadius: '6px',
        borderLeft: `3px solid ${ehResposta ? '#f59e0b' : (ehMeu ? COR.azul : COR.borda)}`,
      }}>
        <div style={{
          width: ehResposta ? '26px' : '32px', height: ehResposta ? '26px' : '32px',
          borderRadius: '50%',
          backgroundColor: ehResposta ? '#f59e0b' : (ehMeu ? COR.azul : COR.muted),
          color: COR.branco, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: ehResposta ? '12px' : '14px', fontWeight: '700', flexShrink: 0,
        }}>{inicial}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
            {ehResposta && <span style={{ color: '#f59e0b', fontSize: '11px' }}>↳</span>}
            <span style={{ fontSize: '12px', fontWeight: '700', color: COR.texto }}>{c.nome_amigavel || c.nome}</span>
            <span style={{ fontSize: '10px', color: COR.muted }}>
              {tempoRelativo(c.criado_em)}{c.editado === 1 && ' (editado)'}
            </span>
            {ehMeu && (
              <button onClick={() => deletar(c.id)} title="Apagar" style={{
                marginLeft: 'auto', border: 'none', background: 'transparent',
                color: COR.muted, cursor: 'pointer', fontSize: '12px', padding: '0 4px',
              }}>🗑</button>
            )}
          </div>
          <div style={{ fontSize: '13px', color: COR.texto, lineHeight: '1.4', wordBreak: 'break-word' }}>
            {renderizarMarkdown(corpoParaExibir(c))}
          </div>
          {anexosNaoInline.length > 0 && (
            <div style={{ marginTop: '4px' }}>
              {anexosNaoInline.map(a => <AnexoBadge key={a.id} anexo={a} />)}
            </div>
          )}
          {podeComentar && (
            <button onClick={() => setModal({ parentId: ehResposta ? c.parent_id : c.id })} style={{
              marginTop: '6px', border: 'none', background: 'transparent',
              color: COR.azul, cursor: 'pointer', fontSize: '11px', fontWeight: '600', padding: 0,
            }}>↳ Responder</button>
          )}
        </div>
      </div>
    );
  };

  // r181: apontamento de horas renderizado como comentário (só leitura —
  // incluir/editar/excluir continua na aba de Apontamentos da tarefa).
  const renderApontamento = (a) => {
    const faturavel = ehFaturavel(a.faturavel);
    const inicial = (a.usuario_nome || '?').charAt(0).toUpperCase();
    return (
      <div key={`ap-${a.id}`} style={{
        display: 'flex', gap: '10px',
        marginBottom: '10px', padding: '8px 10px',
        backgroundColor: COR.verdeBg,
        borderRadius: '6px',
        borderLeft: `3px solid ${COR.verdeHoras}`,
      }}>
        <div style={{
          width: '32px', height: '32px', borderRadius: '50%',
          backgroundColor: COR.verdeHoras, color: COR.branco,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '14px', fontWeight: '700', flexShrink: 0,
        }}>{inicial}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: COR.texto }}>
              {a.usuario_nome || '—'}
            </span>
            <span style={{ fontSize: '10px', color: COR.muted }}>{tempoRelativo(a.criado_em)}</span>
          </div>
          {/* Cabeçalho de tags */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '4px' }}>
            <TagApontamento texto="Apontamento" cor={COR.verdeHoras} solida />
            <TagApontamento texto={faturavel ? 'Faturável' : 'Não Faturável'}
                            cor={faturavel ? COR.azul : COR.muted} />
            <TagApontamento texto={horasParaHHMM(a.horas)} cor={COR.verdeHoras} />
            <TagApontamento texto={`Início: ${dataHora(a.data_inicio)}`} cor={COR.muted} />
            <TagApontamento texto={`Fim: ${dataHora(a.data_fim)}`} cor={COR.muted} />
          </div>
          {a.descricao && (
            <div style={{ fontSize: '13px', color: COR.texto, lineHeight: '1.4', wordBreak: 'break-word' }}>
              {a.descricao}
            </div>
          )}
        </div>
      </div>
    );
  };

  const raizes = comentarios.filter(c => !c.parent_id);
  const respostasDe = (rootId) => comentarios.filter(c => String(c.parent_id) === String(rootId));

  // r181: comentários-raiz e apontamentos numa única linha do tempo, ordenada
  //       pela data de criação (as respostas seguem penduradas na sua raiz).
  // r200: o filtro escolhe o que entra e a ordem escolhe a direção.
  const mostraComentarios = prefs.filtro === 'C' || prefs.filtro === 'T';
  const mostraHoras       = prefs.filtro === 'H' || prefs.filtro === 'T';
  const linhaDoTempo = [
    ...(mostraComentarios ? raizes.map(c => ({ tipo: 'comentario', em: c.criado_em, dado: c })) : []),
    ...(mostraHoras ? apontamentos.map(a => ({ tipo: 'apontamento', em: a.criado_em || a.data_inicio, dado: a })) : []),
  ].sort((x, y) => {
    const dif = new Date(x.em || 0) - new Date(y.em || 0);
    return prefs.ordem === 'desc' ? -dif : dif;
  });

  // Texto do vazio muda com o filtro — "nenhum comentário" com o filtro em
  // Horas mandaria o usuário procurar o problema no lugar errado.
  const textoVazio = prefs.filtro === 'H'
    ? 'Nenhum apontamento de horas nesta tarefa.'
    : prefs.filtro === 'T'
      ? 'Nada por aqui ainda — nenhum comentário nem apontamento.'
      : 'Nenhum comentário ainda. Seja o primeiro a comentar.';

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      maxHeight: '600px',
      fontFamily: "'Manrope', sans-serif",
    }}>
      {/* r200: filtro (comentários / horas / todos) + ordem por data.
          As duas escolhas ficam salvas por usuário no localStorage. */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '8px', flexWrap: 'wrap',
        padding: '0 4px 8px', borderBottom: `1px solid ${COR.borda}`,
      }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          {FILTRO_OPCOES.map(op => {
            const ativo = prefs.filtro === op.valor;
            return (
              <button key={op.valor} type="button"
                onClick={() => salvarPrefs({ filtro: op.valor })}
                style={{
                  padding: '4px 12px', fontSize: '11px', fontWeight: '700',
                  border: `1px solid ${ativo ? COR.azul : COR.borda}`,
                  borderRadius: '12px',
                  backgroundColor: ativo ? COR.azul : COR.branco,
                  color: ativo ? COR.branco : COR.muted,
                  cursor: 'pointer', fontFamily: "'Manrope', sans-serif",
                }}>{op.label}</button>
            );
          })}
        </div>
        <button type="button"
          onClick={() => salvarPrefs({ ordem: prefs.ordem === 'asc' ? 'desc' : 'asc' })}
          title={prefs.ordem === 'asc'
            ? 'Ordem crescente (mais antigos primeiro) — clique pra inverter'
            : 'Ordem decrescente (mais recentes primeiro) — clique pra inverter'}
          style={{
            padding: '4px 12px', fontSize: '11px', fontWeight: '600',
            border: `1px solid ${COR.borda}`, borderRadius: '4px',
            backgroundColor: COR.cinzaBg, color: COR.muted, cursor: 'pointer',
            fontFamily: "'Manrope', sans-serif", whiteSpace: 'nowrap',
          }}>
          {prefs.ordem === 'asc' ? '↑ Mais antigos primeiro' : '↓ Mais recentes primeiro'}
        </button>
      </div>

      {/* Lista de mensagens */}
      <div ref={listaRef} style={{
        flex: 1, overflowY: 'auto',
        padding: '8px 4px',
        minHeight: '120px', maxHeight: '450px',
      }}>
        {carregando ? (
          <div style={{ textAlign: 'center', padding: '20px', color: COR.muted, fontSize: '12px' }}>
            Carregando comentários...
          </div>
        ) : linhaDoTempo.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '24px',
            color: COR.muted, fontSize: '12px', fontStyle: 'italic',
          }}>
            {textoVazio}
          </div>
        ) : (
          linhaDoTempo.map(item => (
            item.tipo === 'apontamento' ? renderApontamento(item.dado) : (
              <div key={item.dado.id}>
                {renderComentario(item.dado, false)}
                {respostasDe(item.dado.id).map(rep => renderComentario(rep, true))}
              </div>
            )
          ))
        )}
      </div>

      {/* Erro */}
      {erro && (
        <div style={{
          padding: '6px 10px', marginTop: '4px',
          backgroundColor: '#fff0f0', border: `1px solid ${COR.vermelho}`,
          borderRadius: '4px', color: COR.vermelho,
          fontSize: '11px', fontWeight: '600',
        }}>
          {erro}
        </div>
      )}

      {/* r173: botão que abre o modal de novo comentário */}
      {podeComentar && (
        <div style={{ marginTop: '10px' }}>
          <button type="button" onClick={() => setModal({ parentId: null })} style={{
            padding: '9px 16px', fontSize: '13px', fontWeight: '700',
            border: `1px solid ${COR.azul}`, borderRadius: '6px',
            backgroundColor: COR.azul, color: COR.branco, cursor: 'pointer',
            fontFamily: "'Manrope', sans-serif",
          }}>+ Comentário</button>
        </div>
      )}

      {modal && (
        <ComentarioModal
          tarefaId={tarefaId}
          parentId={modal.parentId}
          usuarioId={usuario?.id}
          mencionaveis={mencs}
          onFechar={() => setModal(null)}
          onSalvo={recarregar}
        />
      )}
    </div>
  );
}

export default ChatComentarios;
